import type { AppPreferences, HydrationRecommendation, User, UserSettings } from "../types/models";
import { birthYearToDate } from "../lib/dates";
import { createIdempotencyKey } from "../lib/idempotency";
import { lengthToCm, weightToKg } from "../lib/units";
import { apiData, type Schema } from "./api";

export type OnboardingResult = Schema<"OnboardingResponse">;

export async function getProfile(): Promise<User | null> {
  const data = await apiData<Schema<"UserProfileResponseEnvelope">>("/me");
  return data.id ? (data as User) : null;
}

export async function updateProfile(body: Schema<"UpdateProfileRequest">): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"UpdateProfileRequest">>("/me/profile", { method: "PUT", body });
}

export async function getSettings(): Promise<UserSettings> {
  return apiData<Schema<"UserSettingsResponseEnvelope">>("/users/me/settings");
}

export async function updateSettings(body: Schema<"UpdateSettingsRequest">): Promise<UserSettings> {
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"UpdateSettingsRequest">>("/users/me/settings", { method: "PATCH", body });
  return getSettings();
}

export type OnboardingFormValues = {
  height: number;
  birthYear: number;
  hydrationReference: "female" | "male";
  preferredWeight?: number;
  customGoalMl?: number;
};

export async function completeOnboarding(values: OnboardingFormValues, preferences: AppPreferences): Promise<OnboardingResult> {
  const body: Schema<"OnboardingRequest"> = {
    height_cm: lengthToCm(values.height, preferences.lengthUnit),
    date_of_birth: birthYearToDate(values.birthYear),
    sex_for_hydration_formula: values.hydrationReference,
    weight_kg: values.preferredWeight ? weightToKg(values.preferredWeight, preferences.weightUnit) : undefined
  };
  const result = await apiData<Schema<"OnboardingResponseEnvelope">, Schema<"OnboardingRequest">>("/users/me/onboarding", {
    method: "POST",
    body,
    idempotencyKey: createIdempotencyKey()
  });
  await updateProfile({
    locale: preferences.locale,
    timezone_mode: preferences.timezoneMode,
    timezone: preferences.timezoneMode === "fixed" ? preferences.fixedTimezone : undefined
  });
  if (values.customGoalMl) {
    await apiData<Schema<"HydrationGoalResponseEnvelope">, Schema<"SetHydrationGoalRequest">>("/hydration/goals", {
      method: "POST",
      body: { goal_ml: values.customGoalMl },
      idempotencyKey: createIdempotencyKey()
    });
  }
  return result;
}

export async function getHydrationRecommendation(): Promise<HydrationRecommendation> {
  return apiData<Schema<"HydrationRecommendationResponseEnvelope">>("/hydration/recommendation");
}
